/**
 * Early Firebase Error Suppression
 * This runs IMMEDIATELY when the module loads, before Firebase SDK is initialized
 * Intercepts console methods at the earliest possible time
 */

// Execute suppression immediately when module loads
if (typeof window !== 'undefined') {
  const originalError = console.error;
  const originalWarn = console.warn;
  const originalLog = console.log;

  // Aggressive suppression - match any Firebase/connection-related message
  const shouldSuppress = (str: string): boolean => {
    if (!str) return false;
    const lower = String(str).toLowerCase();
    
    // Suppress entire categories of messages
    const suppressPatterns = [
      'could not reach cloud firestore',
      'connection failed',
      'this typically indicates',
      'transport errored',
      'webchannel',
      'webchannelconnection',
      'rpc',
      'stream',
      'code=unavailable',
      'offline mode',
      'healthy internet',
      '@firebase/firestore',
      'firestore',
      'listen',
      'firestore: firestore',
      'defaultloghandler',
      'defaultlogging',
      'backend',
      'unavailable',
      'error listening',
      'failed to reach',
    ];
    
    // Check if this is a Firebase message
    const isFirebaseRelated = lower.includes('firebase') || 
                             lower.includes('firestore') || 
                             lower.includes('webchannel') ||
                             lower.includes('connection');
    
    // Only suppress if it's Firebase-related AND matches a pattern
    return isFirebaseRelated && suppressPatterns.some(p => lower.includes(p));
  };

  // Override console.error
  console.error = function(...args: any[]) {
    const msgStr = String(args[0] || '');
    if (!shouldSuppress(msgStr)) {
      originalError.apply(console, args);
    }
  };

  // Override console.warn
  console.warn = function(...args: any[]) {
    const msgStr = String(args[0] || '');
    if (!shouldSuppress(msgStr)) {
      originalWarn.apply(console, args);
    }
  };

  // Override console.log
  console.log = function(...args: any[]) {
    const msgStr = String(args[0] || '');
    // ALWAYS allow debug logs with emoji prefixes (🔥, 📱, etc.)
    const isDebugLog = /^[🔥📱✅❌⚠️🌐🔍🔄📝🚀]/gi.test(msgStr);
    if (isDebugLog || !shouldSuppress(msgStr)) {
      originalLog.apply(console, args);
    }
  };

  // Suppress via global error handler (for uncaught errors)
  if (typeof window !== 'undefined') {
    const originalOnError = window.onerror;
    window.onerror = function(msg: any, ...rest: any[]) {
      const msgStr = String(msg || '');
      if (shouldSuppress(msgStr)) {
        return true; // Suppress the error
      }
      if (originalOnError) {
        return originalOnError.apply(window, [msg, ...rest] as any);
      }
      return false;
    };
  }
}

export default {};
